/**
 * Facebook Pixel Module (rampjs)
 *
 * Self-registering module that injects the Facebook pixel when
 * `partner_tracking.fbid` is present in the response.
 *
 * Faithful port of the previous `cheq.ts` fbid path: it records
 * `window._rampjsFbState` (read by google-rsoc's pingback diagnostics) and
 * calls `initFacebook`. Like the previous ramp.js code this is treated as
 * consent-exempt (no gating) and does NOT check the `ivt` flag — that is
 * intentional to keep behaviour identical pending the Phase-C consent spike.
 */

import { initFacebook } from "@ascent/conversion-tracking";
import type { Response } from "@ascent/papi-core";
import { registerTrackingPartner } from "../../tracking-registry";
import type { TrackingModule } from "../tracking-module";

/** Diagnostics state shared with google-rsoc's pingback instrumentation. */
export type RampJsFbState = {
  fbidPresent: boolean;
  initCalled: boolean;
  initCalledAt?: number;
};

declare global {
  interface Window {
    _rampjsFbState?: RampJsFbState;
  }
}

export const FacebookModule: TrackingModule = {
  id: "facebook",
  name: "Facebook Pixel",

  // Preserves previous ramp.js behaviour: fires regardless of consent state.
  consentExempt: true,
  requiresExplicitConsent: false,

  isEnabled: (response: Response): boolean => {
    return !!response?.partner_tracking?.fbid;
  },

  initialize: (response: Response): void => {
    const fbid = response?.partner_tracking?.fbid;

    // Record diagnostics state even when fbid is absent so google-rsoc's
    // pingbacks report fb_expected/fb_init_called accurately.
    const fbState: RampJsFbState = (window._rampjsFbState ??= {
      fbidPresent: false,
      initCalled: false,
    });
    fbState.fbidPresent = Boolean(fbid);

    if (!fbid) return;

    fbState.initCalled = true;
    fbState.initCalledAt = Date.now();
    void initFacebook(fbid);
  },
};

registerTrackingPartner(FacebookModule);

export { FacebookModule as default };
